import axios, { AxiosError, AxiosRequestHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { store } from '../../../redux/store';
import { resetPermission } from '../../../redux/setInitialPermission/actions/setInitialPermission.action';

const REQUIRED_ENV_VARS = ['VITE_BASE_URL'] as const;

const missingVars = REQUIRED_ENV_VARS.filter((key) => !import.meta.env[key]);

if (missingVars.length > 0) {
    const message = `Missing environment variable${missingVars.length > 1 ? 's' : ''}: ${missingVars.join(
        ', ',
    )}. Please define them in your Vite env files (e.g. .env.local).`;
    console.error(message);
    throw new Error(message);
}

const baseURL = import.meta.env.VITE_BASE_URL!;

const configuredAxios = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
});

axios.defaults.baseURL = baseURL;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';

const applyDefaultHeaders = (config: InternalAxiosRequestConfig<any>) => {
    const headers = config.headers as AxiosRequestHeaders;

    if (config.data instanceof FormData) {
        if (headers['Content-Type']) {
            delete headers['Content-Type'];
        }
    } else {
        if (!headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }
    }

    if (!headers['Accept']) {
        headers['Accept'] = 'application/json';
    }

    return config;
};

const attachAuthHeader = (config: InternalAxiosRequestConfig<any>): InternalAxiosRequestConfig<any> => {
    const accessToken = localStorage.getItem('adminAccessToken');
    applyDefaultHeaders(config);

    if (accessToken) {
        (config.headers as AxiosRequestHeaders).Authorization = `Bearer ${accessToken}`;
    }
    return config;
};

const handleAuthError = (error: AxiosError) => {
    const statusCode = (error.response?.data as { statusCode?: number })?.statusCode || error.response?.status;
    const isLoginRequest = error.config?.url?.includes('/login');

    if (statusCode === 403) {
        window.location.href = '/no-permission';
        return Promise.reject(error);
    }

    if (statusCode === 401 && !isLoginRequest) {
        localStorage.clear();
        store.dispatch(resetPermission());
        window.location.href = '/login';
    }

    if (statusCode === 406 && !isLoginRequest) {
        localStorage.clear();
        store.dispatch(resetPermission());
        window.location.href = '/login';
    }

    return Promise.reject(error);
};

configuredAxios.interceptors.request.use((request: InternalAxiosRequestConfig<any>) => attachAuthHeader(request), (error: AxiosError) =>
    Promise.reject(error),
);

configuredAxios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => handleAuthError(error),
);

axios.interceptors.request.use(
    (request: InternalAxiosRequestConfig<any>) => attachAuthHeader(request),
    (error: AxiosError) => Promise.reject(error),
);

axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => handleAuthError(error),
);

export { configuredAxios as axiosClient, baseURL };
export default configuredAxios;